/**
 * Summary Service
 * Manages cached dashboard totals and insights in users/{uid}/summary
 * Following the Layout Architecture for Firebase Integration
 */

import { db, auth } from '@/config/firebase';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  collection,
  getDocs,
  query,
  where,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { getCurrentMonthTransactions, calculateSummary } from './transactionService';
import { getGoals } from './goalsService';

/**
 * Get user's summary document reference
 * Collection: users/{uid}/summary
 * @param {String} userId 
 * @returns {DocumentReference}
 */
function getUserSummaryRef(userId) {
  if (!userId) throw new Error('User ID required');
  return doc(db, 'users', userId, 'summary', 'current');
}

/**
 * Calculate and cache dashboard summary
 * @param {String} userId 
 * @returns {Promise<Object>} Summary object
 */
export async function calculateAndCacheSummary(userId = null) {
  const uid = userId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  try {
    // Fetch all necessary data
    const [transactions, goals] = await Promise.all([
      getCurrentMonthTransactions(uid),
      getGoals(uid).catch((error) => {
        console.error('Error loading goals for summary calculations:', error);
        return [];
      })
    ]);

    // Calculate financial summary
    const financialSummary = calculateSummary(transactions);

    // Calculate goals summary
    const activeGoals = goals.filter(g => g.status === 'active');
    const completedGoals = goals.filter(g => g.status === 'completed');
    const totalSaved = goals.reduce((sum, g) => sum + (g.currentAmount || 0), 0);
    const totalGoalTarget = goals.reduce((sum, g) => sum + (g.targetAmount || 0), 0);

    // Build comprehensive summary
    const summary = {
      // Financial totals
      totalBalance: financialSummary.balance,
      totalIncome: financialSummary.income,
      totalExpenses: financialSummary.expenses,
      savingsRate: financialSummary.income > 0 
        ? ((financialSummary.balance / financialSummary.income) * 100).toFixed(2)
        : 0,

      // Goals summary
      activeGoalsCount: activeGoals.length,
      completedGoalsCount: completedGoals.length,
      totalGoalsSaved: totalSaved,
      totalGoalsTarget: totalGoalTarget,
      goalsProgress: totalGoalTarget > 0 
        ? ((totalSaved / totalGoalTarget) * 100).toFixed(2)
        : 0,

      // Transaction counts
      transactionCount: transactions.length,
      incomeTransactionCount: transactions.filter(t => t.type === 'income').length,
      expenseTransactionCount: transactions.filter(t => t.type === 'expense').length,

      // Period info
      period: 'current_month',
      periodStart: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString(),
      periodEnd: new Date().toISOString(),

      // Metadata
      lastCalculated: serverTimestamp(),
      version: '1.0.0'
    };

    // Cache to Firestore
    const summaryRef = getUserSummaryRef(uid);
    await setDoc(summaryRef, summary, { merge: true });

    return summary;
  } catch (error) {
    console.error('Error calculating summary:', error);
    throw error;
  }
}

/**
 * Get cached summary from Firestore
 * @param {String} userId 
 * @param {Boolean} forceRefresh - Force recalculation
 * @returns {Promise<Object>}
 */
export async function getCachedSummary(userId = null, forceRefresh = false) {
  const uid = userId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  try {
    const summaryRef = getUserSummaryRef(uid);
    const summaryDoc = await getDoc(summaryRef);

    // If no cache exists or force refresh, calculate new summary
    if (!summaryDoc.exists() || forceRefresh) {
      return await calculateAndCacheSummary(uid);
    }

    const summary = summaryDoc.data();

    // Check if cache is stale (older than 5 minutes)
    const lastCalculated = summary.lastCalculated?.toDate();
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);

    if (!lastCalculated || lastCalculated < fiveMinutesAgo) {
      // Recalculate in background
      calculateAndCacheSummary(uid).catch(console.error);
    }

    return summary;
  } catch (error) {
    console.error('Error getting cached summary:', error);
    // Fallback to calculation if cache fails
    return await calculateAndCacheSummary(uid);
  }
}

/**
 * Get insights for dashboard
 * @param {String} userId 
 * @returns {Promise<Array>}
 */
export async function getDashboardInsights(userId = null) {
  const uid = userId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  try {
    const summary = await getCachedSummary(uid);
    const insights = [];

    // Insight 1: Savings rate
    if (summary.savingsRate > 20) {
      insights.push({
        type: 'success',
        title: 'Great Savings!',
        message: `You're saving ${summary.savingsRate}% of your income this month. Keep it up!`,
        icon: 'TrendingUp',
        color: '#10b981'
      });
    } else if (summary.savingsRate < 10 && summary.totalIncome > 0) {
      insights.push({
        type: 'warning',
        title: 'Low Savings Rate',
        message: `You're only saving ${summary.savingsRate}% of your income. Consider reducing expenses.`,
        icon: 'AlertCircle',
        color: '#f59e0b'
      });
    }

    // Insight 2: Goals progress
    if (summary.activeGoalsCount > 0 && summary.goalsProgress > 50) {
      insights.push({
        type: 'success',
        title: 'Goals on Track',
        message: `You've achieved ${summary.goalsProgress}% of your savings goals. Great progress!`,
        icon: 'Target',
        color: '#06d6a0'
      });
    }

    // Insight 3: Spending vs Income
    if (summary.totalExpenses > summary.totalIncome) {
      insights.push({
        type: 'danger',
        title: 'Spending Alert',
        message: `You've spent more than you earned this month. Review your budget.`,
        icon: 'AlertTriangle',
        color: '#ef476f'
      });
    }

    // Insight 4: Transaction activity
    if (summary.transactionCount === 0) {
      insights.push({
        type: 'info',
        title: 'No Transactions Yet',
        message: 'Start tracking your expenses and income to see insights.',
        icon: 'Info',
        color: '#118ab2'
      });
    }

    return insights;
  } catch (error) {
    console.error('Error getting insights:', error);
    return [];
  }
}

/**
 * Invalidate summary cache (call after transaction/goal changes)
 * @param {String} userId 
 * @returns {Promise<void>}
 */
export async function invalidateSummaryCache(userId = null) {
  const uid = userId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  try {
    await calculateAndCacheSummary(uid);
  } catch (error) {
    console.error('Error invalidating summary cache:', error);
    // Don't throw - cache invalidation is non-critical
  }
}

/**
 * Get spending trends (for charts)
 * @param {String} userId 
 * @param {Number} months - Number of months to analyze
 * @returns {Promise<Array>}
 */
export async function getSpendingTrends(userId = null, months = 6) {
  const uid = userId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  try {
    const trends = [];
    const now = new Date();

    for (let i = months - 1; i >= 0; i--) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
      const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);

      // Query transactions for this month
      const transactionsRef = collection(db, 'users', uid, 'transactions');
      const q = query(
        transactionsRef,
        where('date', '>=', monthStart.toISOString()),
        where('date', '<=', monthEnd.toISOString())
      );

      const snapshot = await getDocs(q);
      const transactions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const summary = calculateSummary(transactions);

      trends.push({
        month: monthDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        monthKey: `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`,
        income: summary.income,
        expenses: summary.expenses,
        balance: summary.balance,
        transactionCount: transactions.length
      });
    }

    return trends;
  } catch (error) {
    console.error('Error getting spending trends:', error);
    return [];
  }
}
